'use client'

import {
  Badge,
  ChevronDown,
  Command,
  CreditCard,
  Download,
  ExternalLink,
  Github,
  Key,
  Laptop2,
  ReceiptIcon,
  Settings,
  Shell,
  UserRoundPen,
  Webhook,
} from 'lucide-react'
import Link from 'next/link'
import { useMemo, useState } from 'react'

import {
  STRIPE_CUSTOMER_PORTAL,
} from '@/constants'
import { FetchUserQuery } from '@/gql/graphql'
import { useTranslation } from '@/i18n/client'
import { isPioneerUser } from '@/service/user'
import { cn } from '@/utils/cn'

import LinkIndicator from '../link/link-indicator'
import Avatar from '../profile/avatar'
import LogoutButton from './logout.button'

interface UserMenuProps {
  githubLink?: string
  profile?: Pick<
    FetchUserQuery['fetchUser'],
    'name' | 'avatar' | 'login' | 'planEndAt'
  >
}

function NavUserLite(props: UserMenuProps) {
  const { githubLink, profile } = props

  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)

  const isPioneer = isPioneerUser(profile ?? {})

  const menuItems = useMemo(() => {
    if (!profile) {
      return []
    }
    const menus: {
      icon: React.ElementType
      label: string
      href: string
      external?: boolean
    }[] = [
      {
        label: t('nav.user.install'),
        icon: Command,
        href: '/install',
      },
      {
        label: 'Profile',
        icon: UserRoundPen,
        href: `/users/${profile.login}/settings/profile`,
      },
      {
        icon: Key,
        label: t('nav.user.apiTokens'),
        href: `/users/${profile.login}/settings/open-token`,
      },
      {
        label: 'Webhooks',
        icon: Webhook,
        href: `/users/${profile.login}/settings/webhooks`,
      },
      {
        icon: Download,
        label: t('nav.user.downloads'),
        href: `/users/${profile.login}/settings/downloads`,
      },
      {
        icon: Laptop2,
        label: t('nav.user.devices'),
        href: `/users/${profile.login}/settings/devices`,
      },
      {
        icon: ReceiptIcon,
        label: t('nav.user.virtualInvoices'),
        href: `/users/${profile.login}/settings/virtual-invoices`,
      },
      {
        label: t('nav.user.shellAliases'),
        icon: Shell,
        href: `/users/${profile.login}/settings/shell-aliases`,
      },
      {
        label: t('nav.user.badges'),
        icon: Badge,
        href: `/users/${profile.login}/settings/badges`,
      },
      {
        icon: Settings,
        label: t('nav.user.settings'),
        href: `/users/${profile.login}/settings`,
      },
    ]

    if (isPioneer) {
      menus.push({
        icon: CreditCard,
        label: t('nav.user.subscription'),
        href: STRIPE_CUSTOMER_PORTAL,
        external: true,
      })
    }

    return menus
  }, [profile, isPioneer, t])

  if (!profile && !githubLink) {
    return null
  }

  if (!profile) {
    return (
      <Link
        href={githubLink!}
        className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg transition',
          !false ?
            'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-400 hover:text-blue-300' :
            'bg-gray-800/50 text-gray-500 cursor-not-allowed'
        )}
      >
        <Github className='h-4 w-4' />
        {t('nav.user.auth')}
      </Link>
    )
  }

  const name = profile.name
  const avatarUrl = profile.avatar

  return (
    <div className='relative'>
      <button
        className='flex items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-700/50'
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
      >
        <Avatar
          name={name}
          avatarUrl={avatarUrl}
          isPioneer={isPioneer}
          size={32}
        />
        <span className='text-gray-200'>{name}</span>
        <ChevronDown className='h-4 w-4 text-gray-400' />
      </button>

      {isOpen && (
        <div className='absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-lg border border-gray-700 bg-gray-800 shadow-lg animate-in fade-in slide-in-from-top-2'>
          <div className='border-b border-gray-700 p-4'>
            <div className='flex items-center gap-3'>
              <Avatar
                name={name}
                avatarUrl={avatarUrl}
                isPioneer={isPioneer}
                size={40}
              />
              <div>
                <div className='font-medium text-gray-200'>{name}</div>
                <Link
                  href={`/users/${profile.login}`}
                  className='text-sm text-gray-400 hover:text-blue-400 hover:underline'
                >
                  @{profile.login}
                </Link>
              </div>
            </div>
          </div>

          <nav className='p-2'>
            {menuItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                className='flex items-center gap-2 rounded-lg px-3 py-2 text-gray-300 transition-colors hover:bg-gray-700/50'
              >
                <LinkIndicator>
                  <item.icon className='h-4 w-4' />
                </LinkIndicator>
                {item.label}
                {item.external && (
                  <ExternalLink className='h-4 w-4 text-gray-400' />
                )}
              </Link>
            ))}

            <LogoutButton login={profile.login} />
          </nav>
        </div>
      )}
    </div>
  )
}

export default NavUserLite
